/*
 * LawFirmSeoTipsTracker
 * All 25 tips from the article as a checklist the reader can work through with their team.
 *
 * Rules:
 * - Ticking a tip marks it done. Progress is shown overall and per group.
 * - "Your next 3" = the first three NOT-done tips in this priority order:
 *     1) the article's three highest-impact tips: 3 (attorney bylines), 20 (review habit),
 *        11 (sub-practice pages)
 *     2) the remaining quick wins: 1, 2, 4, 5
 *     3) everything else in numerical order.
 * - The to-do list is every not-done tip, in numerical order, as plain text the reader can copy.
 *   Copy uses the clipboard only when clicked; if that fails, the reader is told to copy manually.
 * - Nothing is saved: progress resets when the page reloads (stated in the tool).
 * No tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type Tip = { n: number; text: string };
type Group = { name: string; tips: Tip[] };

const GROUPS: Group[] = [
  {
    name: "Quick wins",
    tips: [
      { n: 1, text: "Add structured data (schema) to every practice page and attorney bio" },
      { n: 2, text: "Add an FAQ built from real intake calls to every practice page" },
      { n: 3, text: "Add attorney bylines with bar number and a link to the bio" },
      { n: 4, text: "Post on your Google Business Profile at least once a week" },
      { n: 5, text: "Reply to every Google review within 24 hours" },
    ],
  },
  {
    name: "On-page",
    tips: [
      { n: 6, text: "Use H1s with practice area + city (only where you have a real presence)" },
      { n: 7, text: "Link every blog post to its most relevant practice page" },
      { n: 8, text: "Use semantic HTML and ARIA landmarks" },
      { n: 9, text: "Add a table of contents to pages over 1,500 words" },
      { n: 10, text: "Compress images, serve WebP and lazy-load them" },
    ],
  },
  {
    name: "Content",
    tips: [
      { n: 11, text: "Build case-type sub-practice pages (car, truck, motorcycle…)" },
      { n: 12, text: "Add anonymized outcomes (check your state bar rules first)" },
      { n: 13, text: "Cite statutes and case law inline" },
      { n: 14, text: "Add a “last reviewed by [attorney]” date" },
      { n: 15, text: "Only change dates when content is really updated" },
    ],
  },
  {
    name: "Local",
    tips: [
      { n: 16, text: "List in legal directories: Avvo, Justia, FindLaw, Martindale, Super Lawyers, state bar" },
      { n: 17, text: "Build city pages only where you have a real office or attorney" },
      { n: 18, text: "Use city + practice in title tags and H1s" },
      { n: 19, text: "Embed a Google Map on every location page" },
      { n: 20, text: "Build a steady review habit" },
    ],
  },
  {
    name: "Technical and links",
    tips: [
      { n: 21, text: "Remove render-blocking JavaScript above the fold" },
      { n: 22, text: "Move to HTTP/3 if your host supports it; use modern image formats" },
      { n: 23, text: "Use legal news moments as PR hooks for editorial links" },
      { n: 24, text: "Sponsor relevant non-profits and community programs" },
      { n: 25, text: "Stop buying directory link packages" },
    ],
  },
];

const ALL: Tip[] = GROUPS.flatMap((g) => g.tips);
const PRIORITY: number[] = [3, 20, 11, 1, 2, 4, 5, ...ALL.map((t) => t.n).filter((n) => ![3, 20, 11, 1, 2, 4, 5].includes(n))];

export default function LawFirmSeoTipsTracker() {
  const baseId = useId();
  const [done, setDone] = useState<Record<number, boolean>>({});
  const [copyStatus, setCopyStatus] = useState("");

  const doneCount = ALL.filter((t) => done[t.n]).length;
  const next3 = PRIORITY.filter((n) => !done[n])
    .slice(0, 3)
    .map((n) => ALL.find((t) => t.n === n) as Tip);
  const remaining = ALL.filter((t) => !done[t.n]);
  const todoText = remaining.map((t) => `Tip ${t.n}: ${t.text}`).join("\n");

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(todoText);
      setCopyStatus("Copied. Paste it into your team's task list.");
    } catch {
      setCopyStatus("Couldn't copy automatically. Select the text and copy it manually.");
    }
  };

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Your 25-tip checklist</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Tick what your firm already does. Nothing is saved, so copy your to-do list before you leave the page.
      </p>

      <p className="mt-4 text-sm font-medium" aria-live="polite">
        {doneCount} of {ALL.length} done
      </p>
      <div className="mt-2 h-2 w-full rounded-full bg-muted" aria-hidden="true">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${(doneCount / ALL.length) * 100}%` }} />
      </div>

      <div className="mt-5 space-y-5">
        {GROUPS.map((g) => {
          const gDone = g.tips.filter((t) => done[t.n]).length;
          const legendId = `${baseId}-${g.name.replace(/\s+/g, "-").toLowerCase()}`;
          return (
            <fieldset key={g.name} aria-labelledby={legendId}>
              <div className="flex items-baseline justify-between">
                <span id={legendId} className="text-sm font-semibold">
                  {g.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {gDone}/{g.tips.length}
                </span>
              </div>
              <ul className="mt-2 space-y-2">
                {g.tips.map((t) => (
                  <li key={t.n}>
                    <Button variant="outline"
                      type="button"
                      aria-pressed={!!done[t.n]}
                      onClick={() => setDone((p) => ({ ...p, [t.n]: !p[t.n] }))}
                      className={`h-auto w-full justify-start whitespace-normal rounded-lg border px-3 py-2 text-left text-sm ${focusRing} ${
                        done[t.n] ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"
                      }`}
                    >
                      <span aria-hidden="true">{done[t.n] ? "✓ " : "○ "}</span>
                      <span className="font-medium">{t.n}.</span> {t.text}
                    </Button>
                  </li>
                ))}
              </ul>
            </fieldset>
          );
        })}
      </div>

      <div aria-live="polite" className="mt-6 rounded-lg bg-muted p-4">
        {remaining.length === 0 ? (
          <p className="text-sm">
            All 25 done. The next gains come from doing them consistently and measuring the results.{" "}
            <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
              Get a free SEO audit →
            </Link>
          </p>
        ) : (
          <div>
            <p className="text-sm font-semibold">Your next 3</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm">
              {next3.map((t) => (
                <li key={t.n}>
                  Tip {t.n}: {t.text}
                </li>
              ))}
            </ol>

            <label htmlFor={`${baseId}-todo`} className="mt-4 block text-sm font-medium">
              Your to-do list ({remaining.length} left)
            </label>
            <Textarea
              id={`${baseId}-todo`}
              readOnly
              rows={Math.min(10, remaining.length)}
              value={todoText}
              className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-xs ${focusRing}`}
            />
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button variant="outline"
                type="button"
                onClick={copy}
                className={`rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ${focusRing}`}
              >
                Copy to-do list
              </Button>
              {copyStatus && <span className="text-sm">{copyStatus}</span>}
            </div>
            <p className="mt-4 text-sm">
              Want us to work through the list with you?{" "}
              <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Get a free SEO audit →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
